/**
 * Sistema de partículas do legado (`par.*`: `Emitter`, `Particle`, `ParticleEnginee`, `AbstractLifeEasing`). Cuida só da simulação; o desenho fica no renderer.
 * O legado avança um passo fixo de 0,04 s por quadro (`ParticleEnginee.update`). Aqui o passo é o mesmo, para o rastro ter o mesmo espaçamento.
 * As fórmulas seguem o ActionScript, inclusive os efeitos que parecem descuido (a velocidade e o peso se multiplicam pela curva a cada passo;
 * `motionV` usa a base para sortear a variação). Isso mantém o rastro igual ao do jogo original.
 */

/** Curva de vida: pontos `[x, y]` com x de 0 a 1 (fração da vida da partícula). Vazia vale 1. */
export type Line = readonly (readonly [number, number])[];

export interface ParticleDef {
  name: string;
  beginTime: number;
  endTime: number;
  /** Pares `[base, variação]`: o valor sorteado vai de base a base + variação. */
  life: readonly [number, number];
  count: readonly [number, number];
  size: readonly [number, number];
  v: readonly [number, number];
  weight: readonly [number, number];
  spin: readonly [number, number];
  motionV: number;
  alpha: number;
  color: number;
  /** Com `keepColor`, a cor soma ao desenho da forma; sem ele, a forma vira uma silhueta da cor. */
  keepColor: boolean;
  keepOldFirst: boolean;
  blend: string;
  /** Índice da forma (`displayCreator`): a arte é `ParticalShap<shape + 1>` do `shape.swf`. */
  shape: number;
  rotation: number;
  easing: { v?: Line; rv?: Line; sp?: Line; size?: Line; weight?: Line; alpha?: Line; color?: Line };
}

export interface EmitterDef {
  beginAngle: number;
  endAngle: number;
  /** Segundos entre uma emissão e a seguinte. */
  interval: number;
  /** Vida do emissor em segundos; 0 ou menos = enquanto existir. */
  life: number;
  particles: readonly ParticleDef[];
}

export interface Particle {
  def: ParticleDef;
  x: number;
  y: number;
  alpha: number;
  color: number;
  scale: number;
  rotation: number;
  life: number;
  age: number;
  size: number;
  v: number;
  angle: number;
  gv: number;
  motionV: number;
  weight: number;
  spin: number;
}

/** Passo fixo da simulação, em segundos (`ParticleEnginee.update`). */
export const PARTICLE_STEP = 0.04;
/** Teto de partículas vivas (`ParticleEnginee._maxCount`): passando dele, as mais antigas saem. */
export const PARTICLE_MAX = 200;

/** `XLine.interpolate`: interpolação linear entre os dois pontos que cercam `t`; depois do último ponto a reta continua (extrapola), como no legado. */
export function interpolateLine(line: Line | undefined, t: number): number {
  if (!line || line.length === 0) return 1;
  if (line.length === 1) return line[0]![1];
  if (line.length === 2 && line[0]![1] === line[1]![1]) return line[0]![1];
  let i = 1;
  while (i < line.length && line[i]![0] <= t) i++;
  const p1 = line[Math.min(i, line.length - 1)]!;
  const p0 = line[Math.min(i, line.length - 1) - 1]!;
  return ((t - p0[0]) * (p1[1] - p0[1])) / (p1[0] - p0[0]) + p0[1];
}

const channel = (v: number) => Math.max(0, Math.min(255, Math.round(v)));

/** `ColorLine.interpolate`: mistura canal a canal as cores dos dois pontos que cercam `t`. */
export function interpolateColorLine(line: Line, t: number): number {
  if (line.length === 0) return 1;
  if (line.length === 1) return line[0]![1];
  if (line.length === 2 && line[0]![1] === line[1]![1]) return line[0]![1];
  let i = 1;
  while (i < line.length && line[i]![0] <= t) i++;
  const p1 = line[Math.min(i, line.length - 1)]!;
  const p0 = line[Math.min(i, line.length - 1) - 1]!;
  const k = (t - p0[0]) / (p1[0] - p0[0]);
  const mix = (shift: number) => channel(((p0[1] >>> shift) & 255) * (1 - k) + ((p1[1] >>> shift) & 255) * k);
  return (mix(16) << 16) | (mix(8) << 8) | mix(0);
}

/** Emissor vivo: a posição e o ângulo seguem o projétil. */
export class Emitter {
  x = 0;
  y = 0;
  angle = 0;
  /** Verdadeiro depois de `remove`: o emissor para de soltar partículas, e as já soltas terminam a vida. */
  removed = false;
  private age = 0;
  private interval: number;

  constructor(readonly def: EmitterDef) {
    this.interval = def.interval;
  }

  remove(): void {
    this.removed = true;
  }

  /** `Emitter.execute`: avança o relógio e solta partículas quando o intervalo passa. */
  execute(dt: number, emit: (e: Emitter) => void): void {
    this.age += dt;
    if (this.def.life <= 0 || this.age < this.def.life) {
      this.interval += dt;
      if (this.interval > this.def.interval) {
        this.interval = 0;
        emit(this);
      }
    } else {
      this.removed = true;
    }
  }

  get time(): number {
    return this.age;
  }
}

export class ParticleSystem {
  particles: Particle[] = [];
  private emitters: Emitter[] = [];

  constructor(
    private readonly random: () => number = Math.random,
    private readonly maxCount: number = PARTICLE_MAX,
  ) {}

  addEmitter(def: EmitterDef): Emitter {
    const emitter = new Emitter(def);
    this.emitters.push(emitter);
    return emitter;
  }

  get active(): boolean {
    return this.particles.length > 0 || this.emitters.length > 0;
  }

  reset(): void {
    this.particles = [];
    this.emitters = [];
  }

  private rand(from: number, to: number): number {
    return this.random() * (to - from) + from;
  }

  /** `Emitter.emit`: cria as partículas de cada definição que está na janela de tempo. */
  private emit(e: Emitter): void {
    for (const def of e.def.particles) {
      if (!(def.beginTime < e.time && def.endTime > e.time)) continue;
      const count = def.count[0] + Math.trunc(this.rand(0, def.count[1]));
      for (let i = 0; i < count; i++) {
        this.particles.push({
          def,
          x: e.x,
          y: e.y,
          alpha: def.alpha,
          color: def.color,
          scale: 1,
          rotation: def.rotation + e.angle,
          life: def.life[0] + this.rand(0, def.life[1]),
          age: 0,
          size: def.size[0] + this.rand(0, def.size[1]),
          v: def.v[0] + this.rand(0, def.v[1]),
          angle: e.angle + this.rand(e.def.beginAngle, e.def.endAngle),
          gv: 0,
          motionV: def.motionV + this.rand(0, def.motionV),
          weight: def.weight[0] + this.rand(0, def.weight[1]),
          spin: def.spin[0] + this.rand(0, def.spin[1]),
        });
      }
    }
  }

  /** `Particle.update`. */
  private update(p: Particle, dt: number): void {
    const t = p.age / p.life;
    const easing = p.def.easing;
    p.v *= interpolateLine(easing.v, t);
    p.motionV *= interpolateLine(easing.rv, t);
    p.weight *= interpolateLine(easing.weight, t);
    p.gv += p.weight;
    const jitter = this.rand(0, 2 * Math.PI);
    p.x += (p.v * Math.cos(p.angle) + p.motionV * Math.cos(jitter)) * dt;
    p.y += (p.v * Math.sin(p.angle) + p.motionV * Math.sin(jitter) + p.gv) * dt;
    p.scale = p.size * interpolateLine(easing.size, t);
    p.rotation += p.spin * interpolateLine(easing.sp, t) * dt;
    if (easing.color) p.color = interpolateColorLine(easing.color, t);
    p.alpha = interpolateLine(easing.alpha, t);
  }

  /** `ParticleEnginee.update`: um passo de `PARTICLE_STEP` segundos. */
  step(dt: number = PARTICLE_STEP): void {
    while (this.particles.length > this.maxCount) this.particles.shift();
    for (const e of this.emitters) if (!e.removed) e.execute(dt, (em) => this.emit(em));
    this.emitters = this.emitters.filter((e) => !e.removed);
    const alive: Particle[] = [];
    for (const p of this.particles) {
      p.age += dt;
      if (p.age >= p.life) continue;
      this.update(p, dt);
      alive.push(p);
    }
    this.particles = alive;
  }
}
